import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { Flex, Grid, VerticalDivider } from "src/elements";
import { Metric } from "../Metric/Metric";
import { Tooltip } from "../Tooltip/Tooltip";
import { PrimitiveAtom, useAtom } from "jotai";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "src/i18n";
import type {
  AestheticsReason,
  AestheticsScore,
  ComponentKey,
} from "@CommonUtils/cdtStudio/domain/aestheticsScore";
import type { BlockingAxis } from "@CommonUtils/cdtStudio/domain/aestheticsRules";
import { gradeAesthetics, releaseSpineOf, useStorePogGrading } from "./storePogRelease";
import { ExpectedSales, pogExpectedSales } from "src/pages/CdtStudio/domain/pogExpectedSales";
import {
  categoryPrefixOf,
  namesNoBlock,
  withoutCategory,
} from "src/pages/CdtStudio/domain/plainNames";
import { METRIC_IDS, METRIC_TRANSLATION_KEYS, MetricId } from "./metricDescriptions";
import { failedGatesOf, TOP_REASONS, useAestheticsWording } from "./aestheticsWording";

const TOOLTIP_WIDTH = "320px";

const AMOUNT_FORMAT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const formatExpectedSales = (expectedSales: ExpectedSales | null) =>
  !expectedSales || expectedSales.isPlaceholderData || !expectedSales.skuCount
    ? null
    : AMOUNT_FORMAT.format(expectedSales.total);

const TILE_COLUMNS = METRIC_IDS.map(() => "minmax(0, 1fr)").join(" auto ");

const MetricTile = ({
  id,
  percent,
  value,
  size,
  isLoading,
  emptyReason,
  details,
}: {
  id: MetricId;
  percent?: number | null;
  value?: string | null;
  size?: "default" | "small" | "big";
  isLoading?: boolean;
  emptyReason?: string | null;
  /** Shown under the description: why this plan scored what it did. */
  details?: ReactNode;
}) => {
  const { t } = useTranslation("common");
  const translationKeys = METRIC_TRANSLATION_KEYS[id];
  const title = t(translationKeys.title);

  return (
    <Flex direction="column" width="100%" role="group" aria-label={title} tabIndex={0}>
      <Metric
        title={title}
        percent={percent}
        value={value}
        suffix={id === "aesthetics" ? "" : undefined}
        height={size}
        isLoading={isLoading}
      />

      <Tooltip position="top" width={TOOLTIP_WIDTH} textAlign="left" isUsingFocus>
        {emptyReason ?? (
          <>
            {t(translationKeys.description)}
            {details}
          </>
        )}
      </Tooltip>
    </Flex>
  );
};

const COMPONENT_ORDER: readonly ComponentKey[] = [
  "blocking",
  "cdt",
  "fullness",
  "rulesKept",
  "ergonomics",
  "range",
];

/* Spelt out rather than built from a template so every part is guaranteed a translation in
   both languages. */
const PART_KEY = {
  blocking: "planogramMetrics.aesthetics.parts.blocking",
  cdt: "planogramMetrics.aesthetics.parts.cdt",
  fullness: "planogramMetrics.aesthetics.parts.fullness",
  rulesKept: "planogramMetrics.aesthetics.parts.rulesKept",
  ergonomics: "planogramMetrics.aesthetics.parts.ergonomics",
  range: "planogramMetrics.aesthetics.parts.range",
} as const satisfies Record<ComponentKey, string>;

/**
 * Why the plan scored what it did, in the merchant's own words: what the score is made of,
 * the three biggest reasons points were lost, and any check the plan did not pass. The score
 * supplies the facts; every word comes from the translations.
 */
const BLOCKING_PART_KEY = {
  shape: "planogramMetrics.aesthetics.blockingParts.shape",
  edges: "planogramMetrics.aesthetics.blockingParts.edges",
  columns: "planogramMetrics.aesthetics.blockingParts.columns",
  bands: "planogramMetrics.aesthetics.blockingParts.bands",
} as const;

const percentOf = (value: number) => `${Math.round(value * 100)}%`;

const BAND_SOURCE_KEY = {
  draft: "planogramMetrics.aesthetics.bandSource.draft",
  request: "planogramMetrics.aesthetics.bandSource.request",
  emission: "planogramMetrics.aesthetics.bandSource.emission",
  "legacy fences": "planogramMetrics.aesthetics.bandSource.legacyFences",
} as const satisfies Record<NonNullable<AestheticsScore["bandSource"]>, string>;

const CORE_RANGE_SOURCE_KEY = {
  request: "planogramMetrics.aesthetics.coreRangeSource.request",
  "pog flag": "planogramMetrics.aesthetics.coreRangeSource.pogFlag",
} as const satisfies Record<NonNullable<AestheticsScore["coreRangeSource"]>, string>;
/** A reason as shown: names without the category, and a block the CDT names by a placeholder
 *  ("Unassigned CDT1") left out, so the reason names the blocks left, or none. */
const shownReason = (
  reason: AestheticsReason,
  categoryPrefix: string | null,
): Pick<AestheticsReason, "code" | "values"> => {
  const values = Object.fromEntries(
    Object.entries(reason.values).map(([key, value]) => [
      key,
      typeof value === "string" ? withoutCategory(value, categoryPrefix) : value,
    ]),
  );
  const named = (key: string) =>
    typeof values[key] === "string" && !namesNoBlock(values[key] as string);
  if (reason.code === "blockingPair" || reason.code === "blockingOne") {
    const blocks = ["first", "second"].filter(named).map((key) => values[key]);
    if (blocks.length >= 2) return { code: "blockingPair", values };
    return blocks.length === 1
      ? { code: "blockingOne", values: { first: blocks[0] } }
      : { code: "blockingPlain", values: {} };
  }
  if (reason.code === "orderPlain" && !named("block")) return { code: "cdtPlain", values: {} };
  return { code: reason.code, values };
};

const AestheticsDetails = ({
  score,
  releaseId,
  blocking = null,
  categoryPrefix = null,
}: {
  score: AestheticsScore;
  /** The CDT release the plan was graded against, when it was. */
  releaseId?: number | null;
  /** The blocking the plan's own columns were graded on, without a release. */
  blocking?: BlockingAxis | null;
  /** The "<category> - " the plan's sections carry, left out of the names shown. */
  categoryPrefix?: string | null;
}) => {
  const { t } = useTranslation("common");
  const wording = useAestheticsWording();
  const failed = failedGatesOf(score.gates);
  const blockingDetail = score.components.blocking?.detail ?? {};

  const blockingParts = (Object.keys(BLOCKING_PART_KEY) as (keyof typeof BLOCKING_PART_KEY)[])
    .filter((part) => typeof blockingDetail[part] === "number")
    .map((part) => `${t(BLOCKING_PART_KEY[part])} ${percentOf(blockingDetail[part] as number)}`);

  return (
    <Flex direction="column" gap="6px" data-testid="aesthetics-explanation">
      <div>
        <strong>{t("planogramMetrics.aesthetics.breakdown")}</strong>
        {COMPONENT_ORDER.map((key) => {
          const component = score.components[key];
          if (!component) return null;
          return (
            <div key={key}>
              {t(PART_KEY[key])}
              {": "}
              {component.graded
                ? percentOf(component.score)
                : t("planogramMetrics.aesthetics.notGraded")}
              {key === "blocking" && component.graded && blockingParts.length > 0 && (
                <div data-testid="aesthetics-blocking-parts">{blockingParts.join(" · ")}</div>
              )}
            </div>
          );
        })}
      </div>

      {score.penalties.length > 0 && (
        <div>
          <strong>{wording.reasonsTitle}</strong>
          {score.penalties.slice(0, TOP_REASONS).map((reason) => (
            <div key={reason.code}>
              {wording.reason({ ...reason, ...shownReason(reason, categoryPrefix) })}
            </div>
          ))}
        </div>
      )}

      {failed.length > 0 && (
        <div>
          <strong>{wording.gatesTitle}</strong>
          {failed.map((gate) => (
            <div key={gate.id}>{wording.gate(gate.id)}</div>
          ))}
        </div>
      )}

      {score.bandSource && (
        <div data-testid="aesthetics-band-source">{t(BAND_SOURCE_KEY[score.bandSource])}</div>
      )}

      {score.coreRangeSource && (
        <div data-testid="aesthetics-core-range-source">
          {t(CORE_RANGE_SOURCE_KEY[score.coreRangeSource])}
        </div>
      )}

      <div data-testid="aesthetics-provenance">
        {releaseId
          ? t("planogramMetrics.aesthetics.gradedAgainstRelease", {
              version: score.version,
              release: releaseId,
            })
          : blocking === "horizontal"
            ? t("planogramMetrics.aesthetics.gradedAgainstOwnHorizontal", {
                version: score.version,
              })
            : t("planogramMetrics.aesthetics.gradedAgainstColumns", { version: score.version })}
      </div>
    </Flex>
  );
};

export const PlanogramMetrics = ({
  pogAtom,
  size,
  isLoading,
}: {
  pogAtom: PrimitiveAtom<Pog | null>;
  size?: "default" | "small" | "big";
  isLoading?: boolean;
}) => {
  const { t } = useTranslation("common");
  const [pog] = useAtom(pogAtom);
  const { grading, isLoading: isGradingLoading } = useStorePogGrading();
  /* Graded as the plan's score is stored: against its CDT release's contract (each level on the
     axis the merchant gave it) and what its generation was sent, what it stores without them. */
  const aesthetics = useMemo(() => gradeAesthetics(pog, grading), [pog, grading]);
  const release = grading?.release ?? null;
  const gradedAgainst = release && releaseSpineOf(release) ? release.id : null;
  const expectedSales = useMemo(() => pogExpectedSales(pog), [pog]);
  const categoryPrefix = useMemo(
    () =>
      categoryPrefixOf(
        pog?.planogram.bays.flatMap((bay) =>
          bay.shelves.flatMap((shelf) => shelf.items.map((item) => item.variant)),
        ) ?? [],
      ),
    [pog],
  );

  return (
    <Flex align="center" data-tour="planogram-metrics">
      <Grid gap={(size === "small" && "12px") || "20px"} columns={TILE_COLUMNS}>
        <MetricTile
          id="returnOnSpace"
          percent={pog?.calculateReturnOnSpace()}
          size={size}
          isLoading={isLoading}
        />

        <VerticalDivider size={2} />

        <MetricTile
          id="coreRange"
          percent={pog?.calculateCoreRange()}
          size={size}
          isLoading={isLoading}
          emptyReason={
            pog && pog.calculateCoreRange() === null
              ? t(METRIC_TRANSLATION_KEYS.coreRange.emptyReason)
              : null
          }
        />

        <VerticalDivider size={2} />

        <MetricTile
          id="shelfAlignment"
          percent={pog?.calculateShelfAlignment()}
          size={size}
          isLoading={isLoading}
        />

        <VerticalDivider size={2} />

        <MetricTile
          id="dosMos"
          percent={pog?.calculateDosMos()}
          size={size}
          isLoading={isLoading}
        />

        <VerticalDivider size={2} />

        <MetricTile
          id="aesthetics"
          percent={aesthetics?.score}
          size={size}
          isLoading={isLoading || isGradingLoading}
          emptyReason={
            grading?.releaseFailed
              ? t("planogramMetrics.aesthetics.releaseUnavailable")
              : grading?.sentFailed
                ? t("planogramMetrics.aesthetics.requestUnavailable")
                : null
          }
          details={
            !isGradingLoading && aesthetics ? (
              <AestheticsDetails
                score={aesthetics}
                releaseId={gradedAgainst}
                blocking={grading?.sent?.blocking ?? null}
                categoryPrefix={categoryPrefix}
              />
            ) : null
          }
        />

        <VerticalDivider size={2} />

        <MetricTile
          id="expectedSales"
          value={formatExpectedSales(expectedSales)}
          size={size}
          isLoading={isLoading}
          emptyReason={
            expectedSales && expectedSales.isPlaceholderData
              ? t(METRIC_TRANSLATION_KEYS.expectedSales.emptyReason)
              : null
          }
        />
      </Grid>
    </Flex>
  );
};
